export type DraftValue = boolean | string;

export interface LogDraft {
  fields: Record<string, DraftValue>;
  locationId: string;
  occurrenceId: string | null;
  recordTypeCode: string;
  rows: Array<Record<string, DraftValue>>;
  savedAt: string;
  version: 1;
}

export interface DraftFieldLike {
  checked?: boolean;
  name?: string;
  type?: string;
  value?: string;
}

export const DRAFT_KEY_PREFIX = "safeplate_draft.";

// Health detail never goes into browser storage.
const EXCLUDED_FIELDS: ReadonlySet<string> = new Set([
  "sensitive_medical_note",
  "sensitive_symptoms",
]);
const SKIPPED_TYPES: ReadonlySet<string> = new Set([
  "button",
  "file",
  "image",
  "reset",
  "submit",
]);

export function draftKey(userId: string): string {
  return `${DRAFT_KEY_PREFIX}${userId}`;
}

function browserStorage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function cleanValues(value: unknown): Record<string, DraftValue> | null {
  if (!isRecord(value)) return null;
  const output: Record<string, DraftValue> = {};
  for (const [key, entry] of Object.entries(value))
    if (typeof entry === "boolean" || typeof entry === "string")
      output[key] = entry;
  return output;
}

export function parseDraft(raw: string | null): LogDraft | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isRecord(parsed) || parsed.version !== 1) return null;
  const fields = cleanValues(parsed.fields);
  if (
    !fields ||
    typeof parsed.recordTypeCode !== "string" ||
    !parsed.recordTypeCode ||
    typeof parsed.locationId !== "string" ||
    typeof parsed.savedAt !== "string" ||
    !Array.isArray(parsed.rows) ||
    (parsed.occurrenceId !== null && typeof parsed.occurrenceId !== "string")
  )
    return null;
  const rows: Array<Record<string, DraftValue>> = [];
  for (const row of parsed.rows) {
    const cleaned = cleanValues(row);
    if (!cleaned) return null;
    rows.push(cleaned);
  }
  return {
    fields,
    locationId: parsed.locationId,
    occurrenceId: parsed.occurrenceId,
    recordTypeCode: parsed.recordTypeCode,
    rows,
    savedAt: parsed.savedAt,
    version: 1,
  };
}

export function readDraft(
  userId: string,
  storage: Pick<Storage, "getItem"> | null = browserStorage(),
): LogDraft | null {
  if (!userId || !storage) return null;
  try {
    return parseDraft(storage.getItem(draftKey(userId)));
  } catch {
    return null;
  }
}

export function writeDraft(
  userId: string,
  draft: LogDraft,
  storage: Pick<Storage, "setItem"> | null = browserStorage(),
): boolean {
  if (!userId || !storage) return false;
  try {
    storage.setItem(draftKey(userId), JSON.stringify(draft));
    return true;
  } catch {
    return false;
  }
}

export function clearDraft(
  userId: string,
  storage: Pick<Storage, "removeItem"> | null = browserStorage(),
): void {
  if (!userId || !storage) return;
  try {
    storage.removeItem(draftKey(userId));
  } catch {
    // Storage can be blocked; the draft is simply not kept.
  }
}

export function collectFields(
  elements: ArrayLike<DraftFieldLike>,
): Record<string, DraftValue> {
  const fields: Record<string, DraftValue> = {};
  for (const element of Array.from(elements)) {
    const name = element.name;
    if (!name || EXCLUDED_FIELDS.has(name)) continue;
    if (element.type && SKIPPED_TYPES.has(element.type)) continue;
    fields[name] =
      element.type === "checkbox"
        ? Boolean(element.checked)
        : String(element.value ?? "");
  }
  return fields;
}

export function applyFields(
  elements: ArrayLike<DraftFieldLike>,
  fields: Readonly<Record<string, DraftValue>>,
): void {
  for (const element of Array.from(elements)) {
    const name = element.name;
    if (!name || EXCLUDED_FIELDS.has(name) || !(name in fields)) continue;
    if (element.type && SKIPPED_TYPES.has(element.type)) continue;
    const value = fields[name];
    if (element.type === "checkbox") element.checked = value === true;
    else if (typeof value === "string") element.value = value;
  }
}

export function clearAllDrafts(
  storage: Pick<
    Storage,
    "key" | "length" | "removeItem"
  > | null = browserStorage(),
): void {
  if (!storage) return;
  try {
    const keys: string[] = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key?.startsWith(draftKey(""))) keys.push(key);
    }
    for (const key of keys) storage.removeItem(key);
  } catch {
    // Storage can be unavailable in private windows; drafts are a convenience.
  }
}
